import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'antd';
import { Icon } from 'Components/UI';
import { ApisEnum, ApisEnumLabel } from 'Data/enums/Apis';
import coordlyIcon from 'Static/images/logo-coordly-collapsed.png';
import googleDriveIcon from 'Static/images/google-drive-icon.png';
import dropboxIcon from 'Static/images/dropbox-icon.png';
import oneDriveIcon from 'Static/images/onedrive-icon.png';
import { IUpdateConstructionSiteApiRequest } from 'Data/interfaces/Obra/IUpdateConstructionSiteApiRequest';
import { useQueryUpdateApiObra } from 'Hooks/obras/useQueryUpdateApiObra';
import {
  StyledModal,
  ModalContent,
  IconsSection,
  AppIconWrapper,
  WarningDot,
  DashedLine,
  PrimaryMessage,
  SecondaryMessage,
  EmailBadge,
  ModalFooter,
} from './styles';

interface SameAccountErrorModalProps {
  visible: boolean;
  integrationEmail: string;
  csId: number;
  api: number;
  onCancel: () => void;
}

const getServiceIcon = (api: number): string => {
  switch (api) {
    case ApisEnum.GoogleDrive:
      return googleDriveIcon;
    case ApisEnum.Dropbox:
      return dropboxIcon;
    case ApisEnum.OneDrive:
      return oneDriveIcon;
    default:
      return googleDriveIcon;
  }
};

const SameAccountErrorModal: React.FC<SameAccountErrorModalProps> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('disciplinas');
  const { updateApiObra } = useQueryUpdateApiObra({ dispatch, navigate });

  const [isRedirecting, setIsRedirecting] = useState(false);
  const serviceName = ApisEnumLabel[props.api] || 'Google Drive';
  const serviceIcon = getServiceIcon(props.api);

  const handleReconnect = () => {
    setIsRedirecting(true);

    const request: IUpdateConstructionSiteApiRequest = {
      CsId: props.csId,
      Api: props.api,
      ExistingConstructionSiteTokenId: 0,
      isRedirect: true,
      sameAccountRequired: true,
    };
    updateApiObra(request);
  };

  return (
    <StyledModal
      open={props.visible}
      onCancel={props.onCancel}
      footer={null}
      width={480}
      title={t('sameAccountErrorModal.title')}
      centered
      destroyOnClose
    >
      <ModalContent>
        <IconsSection>
          <AppIconWrapper>
            <img src={coordlyIcon} alt='Coordly' width={40} height={40} />
          </AppIconWrapper>
          <DashedLine />
          <WarningDot>
            <Icon icon='aviso' customColor='var(--Danger-danger, #E2574C)' customSize={16} />
          </WarningDot>
          <DashedLine />
          <AppIconWrapper>
            <img src={serviceIcon} alt={serviceName} width={40} height={40} />
          </AppIconWrapper>
        </IconsSection>

        <PrimaryMessage>
          {t('sameAccountErrorModal.primaryMessage')}
        </PrimaryMessage>
        <SecondaryMessage>
          {t('sameAccountErrorModal.secondaryMessage')}
        </SecondaryMessage>

        <EmailBadge>
          <Icon icon='email' customSize={14} customColor='#595959' />
          <span>{props.integrationEmail}</span>
        </EmailBadge>
      </ModalContent>

      <ModalFooter>
        <Button onClick={props.onCancel}>
          {t('sameAccountErrorModal.cancel')}
        </Button>
        <Button
          type='primary'
          loading={isRedirecting}
          onClick={handleReconnect}
          icon={<Icon icon='link' customSize={14} customColor='#ffffff' />}
        >
          {t('sameAccountErrorModal.reconnect')}
        </Button>
      </ModalFooter>
    </StyledModal>
  );
};

export default SameAccountErrorModal;
